import React, { useState, useRef, useEffect, useCallback } from "react";
import "./HumanSupportChatbot.css";

const HumanSupportChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState("welcome");
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [guestId] = useState(() => "guest_" + Date.now());
  const [isHovered, setIsHovered] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // API Integration State
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [queueId, setQueueId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isAssigned, setIsAssigned] = useState(false);
  const [websocket, setWebsocket] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [assignedAgent, setAssignedAgent] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [queuePosition, setQueuePosition] = useState(null);

  // State for manual checking
  const [isManuallyChecking, setIsManuallyChecking] = useState(true);

  // New state for message polling
  const [lastMessageTime, setLastMessageTime] = useState(null);
  const [isPollingMessages, setIsPollingMessages] = useState(false);

  // Refs for cleanup
  const assignmentTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messagePollingRef = useRef(null);

  // API Configuration
  const API_BASE_URL = "https://api3.made2tech.com";
  const COLLECTION_NAME = "Nexonetics";
  const CHECK_INTERVAL_MS = 6000;
  const MESSAGE_POLLING_INTERVAL = 3000; // 3 seconds for message polling
  const MAX_RETRIES = 50;

  // Clear all timeouts on unmount
  useEffect(() => {
    return () => {
      if (assignmentTimeoutRef.current) {
        clearTimeout(assignmentTimeoutRef.current);
        assignmentTimeoutRef.current = null;
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (messagePollingRef.current) {
        clearInterval(messagePollingRef.current);
        messagePollingRef.current = null;
      }
      if (websocket) {
        websocket.close();
      }
    };
  }, [websocket]);

  // Enhanced message polling function - calls API every 3 seconds
  const pollMessagesFromAPI = useCallback(async () => {
    if (!sessionToken || !isAssigned) {
      console.log("No session token or not assigned, skipping message poll");
      return;
    }

    try {
      setIsPollingMessages(true);
      console.log("Polling messages from API...");

      const response = await fetch(
        `${API_BASE_URL}/api/v1/guest-chat/${COLLECTION_NAME}/guest/chat/messages?page=1&size=50`,
        {
          headers: {
            "session-token": sessionToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Messages API response:", data);

      if (data.data && Array.isArray(data.data)) {
        const formattedMessages = data.data.map((msg) => ({
          id: msg.id,
          text: msg.message_text,
          isUser: msg.sender_type === "guest",
          timestamp: new Date(msg.created_at),
          isSystem: false,
          agentName: msg.sender_type === "agent" ? msg.agent_name : null,
        }));

        // Sort messages by timestamp (oldest first)
        const sortedMessages = formattedMessages.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );

        // Update messages state - replace all messages to avoid duplicates
        setMessages(sortedMessages);
        if (sortedMessages.length > 0) {
          setLastMessageTime(
            sortedMessages[sortedMessages.length - 1].timestamp
          );
        }
      }
    } catch (error) {
      console.error("Error polling messages from API:", error);
      // Don't show error to user for polling failures, just log it
    } finally {
      setIsPollingMessages(false);
    }
  }, [API_BASE_URL, COLLECTION_NAME, sessionToken, isAssigned]);

  // Start message polling when chat is active
  useEffect(() => {
    if (isAssigned && sessionToken && currentView === "chat") {
      console.log("Starting message polling every 3 seconds");

      // Initial load
      pollMessagesFromAPI();

      // Set up interval for every 3 seconds
      messagePollingRef.current = setInterval(() => {
        pollMessagesFromAPI();
      }, MESSAGE_POLLING_INTERVAL);

      return () => {
        if (messagePollingRef.current) {
          console.log("Stopping message polling");
          clearInterval(messagePollingRef.current);
          messagePollingRef.current = null;
        }
      };
    }
  }, [isAssigned, sessionToken, currentView, pollMessagesFromAPI]);

  // Initialize WebSocket function (keep for real-time updates)
  const initializeWebSocket = useCallback(
    async (token = sessionToken) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/guest-chat/${COLLECTION_NAME}/guest/websocket/public`
        );
        const wsInfo = await response.json();

        if (wsInfo.websocket_url) {
          const ws = new WebSocket(wsInfo.websocket_url);

          ws.onopen = () => {
            console.log("WebSocket connected");
            ws.send(
              JSON.stringify({
                type: "auth",
                session_token: token,
              })
            );
          };

          ws.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              console.log("WebSocket message received:", data);

              if (data.type === "message") {
                // Trigger a poll to get the latest messages instead of adding directly
                // This prevents duplicate messages since polling will handle deduplication
                pollMessagesFromAPI();
              } else if (data.type === "typing") {
                console.log("Typing indicator:", data);
              }
            } catch (error) {
              console.error("Error parsing WebSocket message:", error);
            }
          };

          ws.onclose = () => {
            console.log("WebSocket disconnected");
            if (token && sessionToken) {
              setTimeout(() => {
                initializeWebSocket();
              }, 5000);
            }
          };

          ws.onerror = (error) => {
            console.error("WebSocket error:", error);
          };

          setWebsocket(ws);
        }
      } catch (error) {
        console.error("Error initializing WebSocket:", error);
      }
    },
    [API_BASE_URL, COLLECTION_NAME, sessionToken, pollMessagesFromAPI]
  );

  // Load initial messages function
  const loadInitialMessages = useCallback(
    async (token = sessionToken) => {
      if (!token) return;

      try {
        console.log("Loading initial messages...");
        const response = await fetch(
          `${API_BASE_URL}/api/v1/guest-chat/${COLLECTION_NAME}/guest/chat/messages?page=1&size=50`,
          {
            headers: {
              "session-token": token,
            },
          }
        );

        const data = await response.json();

        if (response.ok && data.data) {
          const formattedMessages = data.data.map((msg) => ({
            id: msg.id,
            text: msg.message_text,
            isUser: msg.sender_type === "guest",
            timestamp: new Date(msg.created_at),
            isSystem: false,
            agentName: msg.sender_type === "agent" ? msg.agent_name : null,
          }));

          const sortedMessages = formattedMessages.sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );

          setMessages(sortedMessages);
          if (sortedMessages.length > 0) {
            setLastMessageTime(
              sortedMessages[sortedMessages.length - 1].timestamp
            );
          }
        }
      } catch (error) {
        console.error("Error loading initial messages:", error);
      }
    },
    [API_BASE_URL, COLLECTION_NAME, sessionToken]
  );

  // Clean up function
  const resetChat = useCallback(() => {
    console.log("Resetting chat...");

    if (assignmentTimeoutRef.current) {
      clearTimeout(assignmentTimeoutRef.current);
      assignmentTimeoutRef.current = null;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (messagePollingRef.current) {
      clearInterval(messagePollingRef.current);
      messagePollingRef.current = null;
    }

    setCurrentView("welcome");
    setMessages([]);
    setApiError(null);
    setIsAssigned(false);
    setSessionToken(null);
    setSessionId(null);
    setQueueId(null);
    setAssignedAgent(null);
    setRetryCount(0);
    setQueuePosition(null);
    setIsManuallyChecking(true);
    setLastMessageTime(null);
    setIsPollingMessages(false);

    if (websocket) {
      websocket.close();
      setWebsocket(null);
    }
  }, [websocket]);

  // Check assignment status function
  const checkAssignmentStatus = useCallback(
    async (isManualCheck = false) => {
      if (!queueId) {
        console.log("No queueId available for assignment check");
        return;
      }

      if (!isManualCheck && retryCount >= MAX_RETRIES) {
        if (retryCount >= MAX_RETRIES) {
          setApiError("Connection timeout. Please try starting a new chat.");
          setCurrentView("welcome");
          resetChat();
        }
        return;
      }

      if (isManualCheck) {
        setIsManuallyChecking(true);
      }

      console.log(
        "Checking assignment status for queueId:",
        queueId,
        "Retry count:",
        retryCount,
        "Manual:",
        isManualCheck
      );

      try {
        const url = `${API_BASE_URL}/api/v1/guest-chat/${COLLECTION_NAME}/guest/chat/assignment-status?guest_identifier=${guestId}&queue_id=${queueId}`;
        console.log("Making request to:", url);

        const response = await fetch(url);
        const data = await response.json();

        console.log("Assignment status response:", data);

        if (response.ok) {
          if (data.assigned) {
            console.log("Agent assigned!");
            setIsAssigned(true);
            setSessionToken(data.session_token);
            setSessionId(data.session_id);
            setAssignedAgent({
              name: data.agent_name || "Support Agent",
              id: data.session_id,
            });

            await initializeWebSocket(data.session_token);
            await loadInitialMessages(data.session_token);
            setCurrentView("chat");

            // Add welcome message
            const welcomeMessage = {
              id: Date.now(),
              text: `Hello! I'm ${
                data.agent_name || "your support agent"
              }. How can I help you today?`,
              isUser: false,
              timestamp: new Date(),
              isSystem: false,
              agentName: data.agent_name || "Support Agent",
            };

            setMessages((prev) => [...prev, welcomeMessage]);
          } else {
            console.log("Still in queue, retrying in 6 seconds...");
            setQueuePosition(data.queue_position || null);

            if (!isManualCheck) {
              setRetryCount((prev) => prev + 1);
              assignmentTimeoutRef.current = setTimeout(() => {
                checkAssignmentStatus();
              }, CHECK_INTERVAL_MS);
            }
          }
        } else {
          throw new Error(`Assignment status check failed: ${response.status}`);
        }
      } catch (error) {
        console.error("Error checking assignment status:", error);

        if (!isManualCheck) {
          setRetryCount((prev) => prev + 1);
          assignmentTimeoutRef.current = setTimeout(() => {
            checkAssignmentStatus();
          }, CHECK_INTERVAL_MS);
        }
      } finally {
        if (isManualCheck) {
          setIsManuallyChecking(false);
        }
      }
    },
    [
      queueId,
      retryCount,
      guestId,
      API_BASE_URL,
      COLLECTION_NAME,
      MAX_RETRIES,
      CHECK_INTERVAL_MS,
      resetChat,
      initializeWebSocket,
      loadInitialMessages,
    ]
  );

  // Manual check function
  const handleManualCheck = () => {
    checkAssignmentStatus(true);
  };

  // useEffect to start polling when queueId is set
  useEffect(() => {
    if (queueId && !isAssigned) {
      console.log("Starting assignment status polling for queueId:", queueId);
      checkAssignmentStatus();
    }

    return () => {
      if (assignmentTimeoutRef.current) {
        clearTimeout(assignmentTimeoutRef.current);
        assignmentTimeoutRef.current = null;
      }
    };
  }, [queueId, isAssigned]);

  // Start guest chat function
  const startGuestChat = async () => {
    setIsConnecting(true);
    setApiError(null);
    setCurrentView("connecting");
    setRetryCount(0);

    console.log("Starting guest chat with guestId:", guestId);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/guest-chat/${COLLECTION_NAME}/guest/chat/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            guest_identifier: guestId,
            guest_name: "Guest User",
            guest_email: "",
            initial_message: "I need assistance with support",
            priority: 1,
            guest_metadata: {
              user_agent: navigator.userAgent,
              page_url: window.location.href,
            },
          }),
        }
      );

      const data = await response.json();
      console.log("Start chat response:", data);

      if (response.ok) {
        setQueueId(data.id);
        setQueuePosition(data.queue_position || null);
      } else {
        setApiError(
          `Failed to start chat session: ${data.message || "Unknown error"}`
        );
        console.error("Failed to start chat:", data);
        setCurrentView("welcome");
      }
    } catch (error) {
      setApiError("Network error. Please try again.");
      console.error("Error starting chat:", error);
      setCurrentView("welcome");
    } finally {
      setIsConnecting(false);
    }
  };

  // Send message function
  const sendMessage = async (messageText) => {
    if (!sessionToken || !messageText.trim()) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/guest-chat/${COLLECTION_NAME}/guest/chat/message`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "session-token": sessionToken,
          },
          body: JSON.stringify({
            message_text: messageText,
            message_type: "text",
            reply_to_message_id: 0,
            attachments: [],
            message_metadata: {},
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      console.log("Message sent successfully");
      // Trigger immediate poll after sending message
      setTimeout(pollMessagesFromAPI, 500);
    } catch (error) {
      console.error("Error sending message:", error);
      setApiError("Failed to send message. Please try again.");
    }
  };

  // Send typing indicator
  const sendTypingIndicator = async (isTyping) => {
    if (!sessionToken) return;

    try {
      await fetch(
        `${API_BASE_URL}/api/v1/guest-chat/${COLLECTION_NAME}/guest/chat/typing`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "session-token": sessionToken,
          },
          body: JSON.stringify({
            is_typing: isTyping,
          }),
        }
      );
    } catch (error) {
      console.error("Error sending typing indicator:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 80) + "px";
    }
  }, [inputValue]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !sessionToken) return;

    const messageText = inputValue.trim();
    setInputValue("");

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    await sendTypingIndicator(false);

    // Don't add message to state locally - let polling handle it
    await sendMessage(messageText);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (sessionToken) {
      if (value.length > 0) {
        sendTypingIndicator(true);

        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }

        typingTimeoutRef.current = setTimeout(() => {
          sendTypingIndicator(false);
        }, 6000);
      } else {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
        sendTypingIndicator(false);
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);

    if (assignmentTimeoutRef.current) {
      clearTimeout(assignmentTimeoutRef.current);
      assignmentTimeoutRef.current = null;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (messagePollingRef.current) {
      clearInterval(messagePollingRef.current);
      messagePollingRef.current = null;
    }

    if (websocket) {
      websocket.close();
      setWebsocket(null);
    }
  };

  return (
    <>
      <button
        className={`floating-button ${
          isHovered ? "floating-button:hover" : ""
        }`}
        onClick={() => setIsOpen(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        💬
      </button>

      {isOpen && (
        <div className="chat-dialog">
          <div className="chat-header">
            <div className="header-overlay" />

            <div className="header-title">
              <span>
                {currentView === "welcome"
                  ? "💬"
                  : currentView === "connecting"
                  ? "⏳"
                  : "👤"}
              </span>
              <span>
                {currentView === "welcome"
                  ? "Support Chat"
                  : currentView === "connecting"
                  ? "Connecting..."
                  : assignedAgent?.name || "Support Agent"}
              </span>
            </div>

            <button className="close-button" onClick={handleClose}>
              ✕
            </button>
          </div>

          {currentView === "welcome" && (
            <div className="welcome-container">
              <div className="welcome-title">Welcome to Support</div>
              <div className="welcome-subtitle">
                Get help from our support team. Click below to start a
                conversation with an available agent.
              </div>
              {apiError && <div className="error-message">{apiError}</div>}
              <button
                className={`start-chat-button ${
                  isHovered ? "start-chat-button:hover" : ""
                }`}
                onClick={startGuestChat}
                disabled={isConnecting}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {isConnecting ? "Starting Chat..." : "Start Chat"}
              </button>
            </div>
          )}

          {currentView === "connecting" && (
            <>
              <div className="connecting-status">
                <span>⏳</span>
                <div>
                  <div>Connecting you to support...</div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                    Please wait while we find an available agent
                    {queuePosition && ` (Position in queue: ${queuePosition})`}
                  </div>
                </div>
              </div>

              <div className="connecting-controls">
                <button
                  className={`manual-check-button ${
                    isHovered && !isManuallyChecking
                      ? "manual-check-button:hover"
                      : ""
                  }`}
                  onClick={handleManualCheck}
                  disabled={isManuallyChecking}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {isManuallyChecking ? "Checking..." : "Check Now"}
                </button>
              </div>

              <div className="messages-container">
                <div className="system-message">
                  Please wait while we connect you to a support agent...
                  {retryCount > 0 && (
                    <div
                      style={{
                        fontSize: "11px",
                        marginTop: "4px",
                        opacity: 0.8,
                      }}
                    >
                      Auto-checking every 6 seconds... ({retryCount}/
                      {MAX_RETRIES})
                    </div>
                  )}
                  {queuePosition && (
                    <div className="queue-position">
                      Queue position: {queuePosition}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {currentView === "chat" && (
            <>
              <div className="messages-container">
                {messages.map((message) => (
                  <div key={message.id}>
                    {message.isSystem ? (
                      <div className="system-message">{message.text}</div>
                    ) : (
                      <div
                        className={`message-wrapper ${
                          message.isUser ? "message-wrapper-user" : ""
                        }`}
                      >
                        <div
                          className={`message-avatar ${
                            message.isUser
                              ? "message-avatar-user"
                              : "message-avatar-agent"
                          }`}
                        >
                          {message.isUser ? "You" : "SA"}
                        </div>
                        <div
                          className={`message-bubble ${
                            message.isUser
                              ? "message-bubble-user"
                              : "message-bubble-agent"
                          }`}
                        >
                          {message.text}
                          {message.agentName && (
                            <div
                              style={{
                                fontSize: "10px",
                                opacity: 0.7,
                                marginTop: "4px",
                              }}
                            >
                              - {message.agentName}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="input-container">
                {apiError && <div className="error-message">{apiError}</div>}
                <div className="input-row">
                  <textarea
                    ref={textareaRef}
                    className={`text-input ${
                      inputFocused ? "text-input:focus" : ""
                    }`}
                    placeholder="Type your message..."
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    rows="1"
                    disabled={!sessionToken}
                  />
                  <button
                    className={`send-button ${
                      !inputValue.trim() || !sessionToken
                        ? "send-button:disabled"
                        : inputValue.trim() && isHovered && sessionToken
                        ? "send-button:hover"
                        : ""
                    }`}
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || !sessionToken}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    ➤
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default HumanSupportChatbot;
